<template>
  <v-card>
    <v-card-title class="subtitle-1 fixed-header">
      Service Details: {{ getServiceName }}
      <v-spacer></v-spacer>
      <v-btn
          icon
          class="mr-2"
          @click="$emit('refresh')"
          :disabled="loading"
      >
        <v-icon :class="{'v-icon--spin': loading}">mdi-refresh</v-icon>
      </v-btn>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text>
      <!-- Tasks 정보 섹션 추가 -->
      <v-row>
        <v-col cols="12">
          <h3 class="subtitle-2 mb-3">Tasks Status</h3>
          <v-card outlined>
            <v-card-text class="pa-0">
              <v-data-table
                  :headers="taskHeaders"
                  :items="service._tasks || []"
                  dense
                  disable-pagination
                  hide-default-footer
              >
                <template v-slot:item.Status.State="{ item }">
                  <v-chip
                      x-small
                      :color="getTaskStatusColor(item.Status.State)"
                  >
                    {{ item.Status.State }}
                  </v-chip>
                </template>

                <template v-slot:item.containerStatus="{ item }">
                  <v-chip
                      x-small
                      :color="getContainerStatusColor(item)"
                  >
                    {{ getContainerStatus(item) }}
                  </v-chip>
                </template>

                <template v-slot:item.imageTag="{ item }">
                  <v-chip
                      x-small
                      :color="getImageTagColor(item)"
                      outlined
                  >
                    {{ getImageTag(item) }}
                  </v-chip>
                </template>

                <template v-slot:item.Status.Timestamp="{ item }">
                  {{ formatDate(item.Status.Timestamp) }}
                </template>

                <template v-slot:item.Status.Err="{ item }">
                  <span v-if="item.Status.Err" class="error--text">
                    {{ item.Status.Err }}
                  </span>
                  <span v-else>-</span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- 기존 기본 정보 -->
      <v-row class="mt-4">
        <v-col cols="12">
          <h3 class="subtitle-2 mb-3">Basic Information</h3>
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
              <tr>
                <td class="font-weight-medium">ID</td>
                <td>{{ service.ID }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Name</td>
                <td>{{ service.Spec?.Name }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Created</td>
                <td>{{ formatDate(service.CreatedAt) }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Updated</td>
                <td>{{ formatDate(service.UpdatedAt) }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Stack</td>
                <td>{{ service.Spec?.Labels?.['com.docker.stack.namespace'] || '-' }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Mode</td>
                <td>{{ service.Spec?.Mode?.Global ? 'Global' : 'Replicated' }}</td>
              </tr>
              <tr v-if="service.Spec?.Mode?.Replicated">
                <td class="font-weight-medium">Replicas</td>
                <td>{{ service.Spec.Mode.Replicated.Replicas }}</td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>

      <!-- 기존 컨테이너 스펙 -->
      <v-row class="mt-3">
        <v-col cols="12">
          <h3 class="subtitle-2 mb-3">Container Specification</h3>
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
              <tr>
                <td class="font-weight-medium">Image</td>
                <td>{{ service.Spec?.TaskTemplate?.ContainerSpec?.Image }}</td>
              </tr>
              <tr v-if="service.Spec?.TaskTemplate?.ContainerSpec?.Args?.length">
                <td class="font-weight-medium">Arguments</td>
                <td>
                  <div v-for="arg in service.Spec.TaskTemplate.ContainerSpec.Args" :key="arg">
                    {{ arg }}
                  </div>
                </td>
              </tr>
              <tr v-if="service.Spec?.TaskTemplate?.ContainerSpec?.Command?.length">
                <td class="font-weight-medium">Command</td>
                <td>
                  <div v-for="cmd in service.Spec.TaskTemplate.ContainerSpec.Command" :key="cmd">
                    {{ cmd }}
                  </div>
                </td>
              </tr>
              <tr v-if="service.Spec?.TaskTemplate?.ContainerSpec?.Env">
                <td class="font-weight-medium">Environment</td>
                <td>
                  <div v-for="env in service.Spec.TaskTemplate.ContainerSpec.Env" :key="env">
                    {{ env }}
                  </div>
                </td>
              </tr>
              <tr v-if="service.Spec?.TaskTemplate?.Resources">
                <td class="font-weight-medium">Resources</td>
                <td>
                  <div v-if="service.Spec.TaskTemplate.Resources.Limits">
                    Limits: {{ formatResources(service.Spec.TaskTemplate.Resources.Limits) }}
                  </div>
                  <div v-if="service.Spec.TaskTemplate.Resources.Reservations">
                    Reservations: {{ formatResources(service.Spec.TaskTemplate.Resources.Reservations) }}
                  </div>
                </td>
              </tr>
              <tr v-if="service.Spec?.TaskTemplate?.ContainerSpec?.Healthcheck">
                <td class="font-weight-medium">Healthcheck</td>
                <td>
                  <div>Test: {{ service.Spec.TaskTemplate.ContainerSpec.Healthcheck.Test.join(' ') }}</div>
                  <div>Interval: {{ formatDuration(service.Spec.TaskTemplate.ContainerSpec.Healthcheck.Interval) }}</div>
                  <div>Timeout: {{ formatDuration(service.Spec.TaskTemplate.ContainerSpec.Healthcheck.Timeout) }}</div>
                  <div>Retries: {{ service.Spec.TaskTemplate.ContainerSpec.Healthcheck.Retries }}</div>
                </td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>

      <!-- 네트워크 & 포트 -->
      <v-row class="mt-3">
        <v-col cols="12">
          <h3 class="subtitle-2 mb-3">Network & Ports</h3>
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
              <tr v-if="service.Endpoint?.Spec?.Ports">
                <td class="font-weight-medium">Ports</td>
                <td>
                  <div v-for="port in service.Endpoint.Spec.Ports" :key="`${port.PublishedPort}-${port.TargetPort}`">
                    {{ port.PublishedPort }}:{{ port.TargetPort }}/{{ port.Protocol }}
                  </div>
                </td>
              </tr>
              <tr v-if="service.Endpoint?.VirtualIPs">
                <td class="font-weight-medium">Virtual IPs</td>
                <td>
                  <div v-for="vip in service.Endpoint.VirtualIPs" :key="vip.Addr">
                    {{ vip.Addr }} (Network: {{ vip.NetworkID }})
                  </div>
                </td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>

      <!-- 볼륨 마운트 -->
      <v-row v-if="service.Spec?.TaskTemplate?.ContainerSpec?.Mounts" class="mt-3">
        <v-col cols="12">
          <h3 class="subtitle-2 mb-3">Mounts</h3>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
              <tr>
                <th>Type</th>
                <th>Source</th>
                <th>Target</th>
                <th>Read Only</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="mount in service.Spec.TaskTemplate.ContainerSpec.Mounts" :key="mount.Target">
                <td>{{ mount.Type }}</td>
                <td>{{ mount.Source }}</td>
                <td>{{ mount.Target }}</td>
                <td>
                  <v-icon small :color="mount.ReadOnly ? 'success' : 'error'">
                    {{ mount.ReadOnly ? 'mdi-check' : 'mdi-close' }}
                  </v-icon>
                </td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>

      <!-- Placement 정보 -->
      <v-row v-if="service.Spec?.TaskTemplate?.Placement" class="mt-3">
        <v-col cols="12">
          <h3 class="subtitle-2 mb-3">Placement</h3>
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
              <tr v-if="service.Spec.TaskTemplate.Placement.Constraints">
                <td class="font-weight-medium">Constraints</td>
                <td>
                  <div v-for="constraint in service.Spec.TaskTemplate.Placement.Constraints"
                       :key="constraint">
                    {{ constraint }}
                  </div>
                </td>
              </tr>
              <tr v-if="service.Spec.TaskTemplate.Placement.Platforms">
                <td class="font-weight-medium">Platforms</td>
                <td>
                  <div v-for="(platform, index) in service.Spec.TaskTemplate.Placement.Platforms"
                       :key="index">
                    {{ platform.Architecture || 'any' }}/{{ platform.OS || 'any' }}
                  </div>
                </td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ServiceDetails',

  props: {
    service: {
      type: Object,
      required: true
    },
    loading: {  // loading prop 추가
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      taskHeaders: [
        { text: 'ID', value: 'ID', width: '100px' },
        { text: 'Task State', value: 'Status.State', width: '120px' },
        { text: 'Container Status', value: 'containerStatus', width: '120px' },
        { text: 'Image Tag', value: 'imageTag', width: '100px' },  // 새로운 칼럼 추가
        { text: 'Node', value: 'NodeID', width: '120px' },
        { text: 'Last Updated', value: 'Status.Timestamp', width: '150px' },
        { text: 'Error', value: 'Status.Err' }
      ]
    }
  },

  computed: {
    getServiceName() {
      return this.service?.Spec?.Name?.split('_').pop() || 'Unknown'
    }
  },

  methods: {
    getImageTag(task) {
      const imagePath = task.Spec?.ContainerSpec?.Image || '';
      const tagMatch = imagePath.split(':')[1]?.split('@')[0];
      return tagMatch || '-';
    },
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')

      return `${year}.${month}.${day} ${hours}.${minutes}`
    },

    formatResources(resources) {
      const parts = []
      if (resources.NanoCPUs) {
        parts.push(`${resources.NanoCPUs / 1000000000} CPU`)
      }
      if (resources.MemoryBytes) {
        parts.push(`${this.formatBytes(resources.MemoryBytes)} Memory`)
      }
      return parts.join(', ')
    },

    formatBytes(bytes) {
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let value = bytes
      let unitIndex = 0
      while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024
        unitIndex++
      }
      return `${value.toFixed(1)} ${units[unitIndex]}`
    },

    formatDuration(nanoseconds) {
      const seconds = nanoseconds / 1000000000
      return `${seconds}s`
    },

    getTaskStatusColor(state) {
      const colors = {
        'running': 'success',
        'starting': 'info',
        'pending': 'warning',
        'failed': 'error',
        'shutdown': 'grey',
        'rejected': 'error',
        'preparing': 'info',
        'new': 'info'
      }
      return colors[state.toLowerCase()] || 'grey'
    },

    getContainerStatus(task) {
      if (!task.Status?.ContainerStatus) return 'No Container'
      if (task.Status.ContainerStatus.Running) return 'Running'
      if (task.Status.ContainerStatus.ExitCode !== undefined) {
        return `Exited (${task.Status.ContainerStatus.ExitCode})`
      }
      return 'Unknown'
    },

    getContainerStatusColor(task) {
      if (!task.Status?.ContainerStatus) return 'grey'
      if (task.Status.ContainerStatus.Running) return 'success'
      if (task.Status.ContainerStatus.ExitCode === 0) return 'info'
      if (task.Status.ContainerStatus.ExitCode !== undefined) return 'error'
      return 'grey'
    },
    getImageTagColor(task) {
      const serviceTag = this.service.Spec.TaskTemplate.ContainerSpec.Image.split(':')[1]?.split('@')[0] || 'latest';
      const taskTag = this.getImageTag(task);
      return taskTag === serviceTag ? 'success' : 'warning';
    }
  }
}
</script>

<style scoped>
.service-details-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.fixed-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: #f5f5f5;  /* 옅은 회색 배경 */
  border-bottom: 1px solid #e0e0e0;  /* 하단 경계선 추가 */
}

.scrollable-content {
  overflow-y: auto;
  flex-grow: 1;
  padding-top: 16px !important;  /* 컨텐츠 상단 여백 추가 */
}
.v-icon--spin {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>